import axios from 'axios';
import { Activity, AuditLog } from '../types/activity.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class ActivityService {
  async getRecentActivities(limit = 10): Promise<Activity[]> {
    const response = await api.get<Activity[]>(`/common/activities/recent/`, {
      params: { limit }
    });
    return response.data;
  }

  async getActivities(filters?: {
    activity_type?: string;
    user?: string;
    company?: string;
    team?: string;
    department?: string;
  }): Promise<Activity[]> {
    const response = await api.get<Activity[]>('/common/activities/', {
      params: filters
    });
    return response.data;
  }

  async getUserActivities(userId?: string): Promise<Activity[]> {
    const response = await api.get<Activity[]>('/common/activities/by_user/', {
      params: userId ? { user_id: userId } : {}
    });
    return response.data;
  }

  async getAuditLogs(filters?: {
    action?: string;
    content_type?: string;
    user?: string;
  }): Promise<AuditLog[]> {
    const response = await api.get<AuditLog[]>('/common/audit-logs/', {
      params: filters
    });
    return response.data;
  }

  async getObjectAuditLogs(contentType: string, objectId: string): Promise<AuditLog[]> {
    const response = await api.get<AuditLog[]>('/common/audit-logs/by_object/', {
      params: {
        content_type: contentType,
        object_id: objectId
      }
    });
    return response.data;
  }
}

const activityService = new ActivityService();
export default activityService;